import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import addAPIToken from "./images/addAPIToken.png";
import configurePermissions from "./images/configurePermissions.png";
import copyApiToken from "./images/copyApiToken.png";
import createToken from "./images/createToken.png";
import navigateAccess from "./images/navigateAccess.png";
import permissionsPage from "./images/permissionsPage.png";
import permissionsTip from "./images/permissionsTip.png";
import savePermissions from "./images/savePermissions.png";

const Kandji = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const [showSecret, setShowSecret] = useState(false);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);

  // TODO may need to change this following migration to iru
  const hostedRegion = [{ name: ".eu.kandji.io" }, { name: ".kandji.io" }];
  const formInitialValues = {
    companyName: "",
    apiToken: "",
    hostedRegion: ".eu.kandji.io",
  };
  const formCheckoutSchema = yup.object().shape({
    companyName: yup.string().required("required"),
    apiToken: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    onSubmit(values);
  };
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("kandji");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerKandjiIntegration } = useAPI();
  const APITokenNameExample = "Breeze Security Platform";

  const onSubmit = (values) => {
    setLoading(true);
    // TODO may need to change this following migration to iru
    const fullUrl =
      "https://" + values.companyName + ".api" + values.hostedRegion;
    registerKandjiIntegration(fullUrl, values.apiToken.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Iru integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Iru and prioritize the suggested remediation."
            : ""
        }
        image={logos.IruLogo}
        iconBorderRadius={0}
        name={"Iru (formerly Kandji)"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Iru is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <Box>
                <Formik
                  onSubmit={handleFormSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Login to your Iru console">
                          <Typography variant={"body2"}>
                            Click the button below to login to your Iru console.
                          </Typography>
                          <PrimaryButton
                            onClick={() => {
                              window.open("https://iru.com/login", "_blank");
                            }}
                            style={{ textTransform: "none" }}
                            disabled={guideMode}
                          >
                            Login to Iru
                          </PrimaryButton>
                        </GuidelineStep>
                        <GuidelineStep title="Create a new API token">
                          <Typography variant="body2">
                            Click on your account name in the bottom left corner
                            and then click on “Access”
                          </Typography>{" "}
                          <ScreenshotAccordion
                            images={[
                              {
                                file: navigateAccess,
                                altext: "navigate to access page",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Click on the "<b>API tokens</b>" tab and then click
                            on the "<b>Add Token</b>" button
                          </Typography>{" "}
                          <ScreenshotAccordion
                            images={[
                              {
                                file: addAPIToken,
                                altext: "add api token",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />{" "}
                          <Box
                            display={"flex"}
                            flexDirection={"row"}
                            alignItems={"center"}
                            gap={0.5}
                          >
                            <Typography variant={"body2"}>
                              Give your API token a name, for example: "
                              <b>{APITokenNameExample}</b>"
                            </Typography>

                            <CustomCopyButton
                              copyTitle={"name"}
                              copyText={APITokenNameExample}
                            />
                          </Box>{" "}
                          <Typography variant={"body2"}>
                            and click on "<b>Create</b>".
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: createToken,
                                altext: "create api token",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Copy and paste the API Token here">
                          <Typography variant={"body2"}>
                            Copy the API token by clicking on the copy icon (The
                            API token in the screenshot is just an example)
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: copyApiToken,
                                altext: "copy api token",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />{" "}
                          <Typography variant={"body2"}>
                            Paste the API token here:
                          </Typography>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <TextField
                              fullWidth
                              variant="filled"
                              name="apiToken"
                              label="Iru API Token"
                              value={values.apiToken}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              autoComplete="off"
                              type={showSecret ? "text" : "password"}
                              error={!!touched.apiToken && !!errors.apiToken}
                              helperText={touched.apiToken && errors.apiToken}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowSecret}
                                      onMouseDown={handleMouseDownSecret}
                                    >
                                      {showSecret ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              style={{ width: "700px" }}
                              disabled={guideMode}
                            />
                          </Box>
                        </GuidelineStep>
                        <GuidelineStep title="Set up read-only permissions for the API token">
                          <Typography variant={"body2"}>
                            After copying the API token, click on the "
                            <b>Configure</b>" button to set up the permissions
                            for the API token
                          </Typography>{" "}
                          <ScreenshotAccordion
                            images={[
                              {
                                file: configurePermissions,
                                altext: "configure permissions",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Breeze requires only "<b>Read Only</b>" permissions.
                          </Typography>
                          <Typography variant="body2" fontWeight={"600"}>
                            Ensure all permissions for 'Get' calls are
                            activated, except in the following sections:
                          </Typography>
                          <Typography variant="body2" fontWeight={"600"}>
                            'Device Secrets', 'Device Actions', 'Custom
                            Scripts', 'Self Service'.
                          </Typography>
                          <Typography variant="body2">
                            <b>Tip:</b> Search "<b>GET</b>" in the search bar
                            and collapse the other sections.
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: permissionsTip,
                                altext: "permissions tip",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Then you can quickly select all the "<b>GET</b>"
                            permissions by selecting a permission group.
                          </Typography>
                          <Typography variant={"body2"}>
                            The screenshot shows an example for the Blueprint
                            section
                          </Typography>{" "}
                          <ScreenshotAccordion
                            images={[
                              {
                                file: permissionsPage,
                                altext: "permissions page",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Click on the "<b>Save</b>" button to save the
                            changes
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: savePermissions,
                                altext: "save permissions",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Enter your team's Iru URL">
                          <Typography variant={"body2"}>
                            Enter your team's Iru URL.
                          </Typography>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                              <Typography variant="body1">https://</Typography>
                            </Grid>
                            <Grid
                              item
                              display={"flex"}
                              flexDirection={"row"}
                              gap={1}
                            >
                              <TextField
                                fullWidth
                                variant="filled"
                                name="companyName"
                                label="my-company"
                                value={values.companyName}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={
                                  !!touched.companyName && !!errors.companyName
                                }
                                helperText={
                                  touched.companyName && errors.companyName
                                }
                                disabled={guideMode}
                              />

                              <Select
                                id="hosted-region"
                                label="Region"
                                name="hostedRegion"
                                value={values.hostedRegion}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                sx={{ width: "200px", height: "60px" }}
                                helperText={
                                  touched.hostedRegion && errors.hostedRegion
                                }
                                disabled={guideMode}
                              >
                                {hostedRegion.map((item) => (
                                  <MenuItem value={item.name}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Grid>
                          </Grid>
                        </GuidelineStep>
                        <Box mt={2}>
                          <PrimaryButton type="submit" disabled={guideMode}>
                            Connect
                          </PrimaryButton>
                        </Box>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default Kandji;
